import { type StepForm, WizardField } from './WizardField';

/**
 * ADR-030 — Baseline subsection shown on wizard Steps 2–5 when the project
 * type is "expansion". Hidden for new_startup and retention (baseline = 0 is
 * the intended framing for both).
 *
 * Baselines represent the **existing** activity at the site prior to the
 * expansion. The adapter subtracts them from the reported totals before
 * sending to the calc engine, so the fiscal impact reflects only the
 * incremental project contribution.
 */
interface BaselineField {
  name: string;
  label: string;
  format?: 'currency' | 'percent';
  step?: number;
  min?: number;
  hint?: string;
}

interface Props {
  form: StepForm;
  projectType: string | undefined;
  fields: ReadonlyArray<BaselineField>;
}

export function BaselineSection({ form, projectType, fields }: Props) {
  if (projectType !== 'expansion') return null;
  return (
    <div className="mt-6 rounded-md border border-amber-200 bg-amber-50/60 p-4">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-amber-900">Existing baseline (expansion only)</h4>
        <p className="mt-0.5 text-xs text-amber-800">
          Enter the current values at the site before this expansion. The fiscal impact will reflect
          only the incremental change (reported − baseline).
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {fields.map((f) => (
          <WizardField
            key={f.name}
            form={form}
            name={f.name}
            label={f.label}
            format={f.format}
            type={f.format ? undefined : 'number'}
            min={f.min ?? 0}
            step={f.step}
            hint={f.hint}
          />
        ))}
      </div>
    </div>
  );
}
